/**
 * Terminal 82 - Card Builder
 *
 * Main application entry point.
 * Redesigned with card-centric two-column layout.
 */

import './styles/index.css';
import './predictions/modal.css';
import './predictions/price-picker.css';
import './predictions/winner-banner.css';
import './notes/notes.css';
import { initModal, renderModal, bindModalEvents, setCurrentPrice, getPredictButtonLabel } from './predictions/modal';
import { showWinnerBanner, type WinnerBannerData } from './predictions/winner-banner';
import { getUser, getAuthToken } from './predictions/auth';
import { inject } from '@vercel/analytics';
import { initSentry } from './lib/sentry';

import type { DayData, CardConfig, ThemeId } from './types';
import { getRarityConfig } from './types';
import { renderCardTo, formatDisplayDate, formatDayId, formatPrice, formatPercentChange } from './card';
import { createHistoricalChart } from './ui/chart';
import { fetchHistoricalData, fetchIntradayData } from './api';
import {
  getDisplayRarity,
  calculateTraits,
} from './stats';
import { renderHeader, renderFooter, renderLoader } from './ui/layout';
import { initNotes, refreshNotes } from './notes';
import { fetchNotes } from './notes/api';

// Declare global constant set by Vite
declare const __ENABLE_EXPORT__: boolean;

// Conditionally import export modules only when enabled
const exportEnabled = typeof __ENABLE_EXPORT__ !== 'undefined' && __ENABLE_EXPORT__;

// Dynamic imports for export functionality (tree-shaken when disabled)
// Server-side export only - client-side has CSS 3D rendering issues
let downloadCardAsMP4Server: typeof import('./export/server-export').downloadCardAsMP4Server | null = null;
let downloadCardAsPngServer: typeof import('./export/server-export').downloadCardAsPngServer | null = null;
let saveExportsForMint: typeof import('./export/server-export').saveExportsForMint | null = null;
let checkExportServer: typeof import('./export/server-export').checkExportServer | null = null;

async function loadExportModules(): Promise<void> {
  if (!exportEnabled) return;

  const serverExportModule = await import('./export/server-export');

  downloadCardAsMP4Server = serverExportModule.downloadCardAsMP4Server;
  downloadCardAsPngServer = serverExportModule.downloadCardAsPngServer;
  saveExportsForMint = serverExportModule.saveExportsForMint;
  checkExportServer = serverExportModule.checkExportServer;
}

// ============================================
// Application State
// ============================================

interface AppState {
  date: string;
  theme: ThemeId;
  data: DayData | null;
  historicalData: DayData[];
  selectedIndex: number;
  isLoading: boolean;
  hasUserInteracted: boolean;  // Track if user has clicked (vs auto-selection)
  exportServerAvailable: boolean;  // Track if Puppeteer export server is available
}

const state: AppState = {
  date: new Date().toISOString().split('T')[0],
  theme: 'legendary',
  data: null,
  historicalData: [],
  selectedIndex: -1,
  isLoading: true,
  hasUserInteracted: false,
  exportServerAvailable: false,
};

// ============================================
// Main Application
// ============================================

async function init(): Promise<void> {
  // Initialize error monitoring
  initSentry();
  
  // Initialize Vercel Analytics
  inject();

  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) return;

  const showNotes = true;

  // Create new card-centric layout
  // Page UI uses pip-boy (green) theme, but card-panel is ISOLATED to prevent CSS leakage
  app.innerHTML = `
    <div class="builder">
      ${renderHeader({
        showPredictButton: new URLSearchParams(window.location.search).has('predict'),
        navLinks: [
          { href: '/', label: 'Explorer', active: true },
          { href: '/collection.html', label: 'Collection' },
          { href: '/tier-preview.html', label: 'Rarity' },
          { href: '/about.html', label: 'About' },
        ],
      })}

      <!-- Left column: Chart and controls -->
      <div class="chart-panel">
        <div class="chart-wrapper">
          <div class="chart-container" id="chart-container">
            <div class="chart-loading">${renderLoader('LOADING ARCHIVE...')}</div>
          </div>
          <div class="chart-hint hidden" id="chart-hint">Scroll · Drag · Select</div>
        </div>

        <!-- Stats bar -->
        <div class="stats-bar" id="stats-bar">
          <div class="stat">
            <span class="stat-label">DATE</span>
            <span class="stat-value" id="stat-date">--</span>
          </div>
          <div class="stat">
            <span class="stat-label">DAY</span>
            <span class="stat-value" id="stat-dayid">--</span>
          </div>
          <div class="stat">
            <span class="stat-label">CLOSE</span>
            <span class="stat-value" id="stat-close">--</span>
          </div>
          <div class="stat">
            <span class="stat-label">CHANGE</span>
            <span class="stat-value" id="stat-change">--</span>
          </div>
        </div>

        <!-- Quick navigation -->
        <div class="quick-nav" id="quick-nav">
          <button class="nav-btn" id="btn-prev">
            <span class="nav-icon">←</span> PREV
          </button>
          <button class="nav-btn" id="btn-next">
            NEXT <span class="nav-icon">→</span>
          </button>
        </div>
      </div>

      <!-- Right column: Card hero - NO theme wrapper, card controls its own theme -->
      <div class="card-panel">
        <div class="card-frame">
          <div class="export-area" id="export-area">
            <div id="card-container">
              <div class="card-loading">
                <span class="loading-title">EXPLORE THE ARCHIVE</span>
                <span class="loading-hint">Click any candle to preview its card. Each day has a unique rarity.</span>
              </div>
            </div>
          </div>
        </div>
        <div class="card-actions">
          ${exportEnabled ? `
          <div class="export-controls">
            <button class="download-btn" id="download-png">
              <span class="download-icon">&gt;</span>
              PNG
            </button>
            <button class="download-btn" id="download-mp4">
              <span class="download-icon">&gt;</span>
              MP4
            </button>
            <select class="minter-select" id="minter-select" title="Who is minting this card?">
              <option value="">No Minter</option>
              <option value="Human">Human</option>
              <option value="Agent">Agent</option>
            </select>
            <input type="text" class="message-input" id="message-input" placeholder="Message (optional)" title="Optional message from minter" maxlength="280" />
            <button class="download-btn primary" id="save-exports" title="Save PNG+MP4 to exports/ folder for minting">
              <span class="download-icon">⬇</span>
              SAVE
            </button>
          </div>
          ` : ''}
          <span class="card-rarity" id="card-rarity"></span>
        </div>
        ${exportEnabled ? `
        <div class="export-progress" id="export-progress" style="display: none;">
          <div class="progress-label" id="progress-label">Capturing frames...</div>
          <div class="progress-bar">
            <div class="progress-fill" id="progress-fill"></div>
          </div>
          <div class="progress-percent" id="progress-percent">0%</div>
        </div>
        ` : ''}
      </div>

      ${showNotes ? '<section class="notes-section" id="notes-section"></section>' : ''}

      ${renderFooter()}
    </div>
  `;

  // Get DOM elements
  const chartContainer = document.querySelector<HTMLDivElement>('#chart-container');
  const cardContainer = document.querySelector<HTMLDivElement>('#card-container');
  const exportArea = document.querySelector<HTMLDivElement>('#export-area');
  const btnPrev = document.querySelector<HTMLButtonElement>('#btn-prev');
  const btnNext = document.querySelector<HTMLButtonElement>('#btn-next');

  // Export elements (only exist when export is enabled)
  let downloadPng: HTMLButtonElement | null = null;
  let downloadMp4: HTMLButtonElement | null = null;
  let saveExports: HTMLButtonElement | null = null;
  let minterSelect: HTMLSelectElement | null = null;
  let messageInput: HTMLInputElement | null = null;
  let exportProgress: HTMLDivElement | null = null;
  let progressLabel: HTMLDivElement | null = null;
  let progressFill: HTMLDivElement | null = null;
  let progressPercent: HTMLDivElement | null = null;

  if (exportEnabled) {
    downloadPng = document.querySelector<HTMLButtonElement>('#download-png');
    downloadMp4 = document.querySelector<HTMLButtonElement>('#download-mp4');
    saveExports = document.querySelector<HTMLButtonElement>('#save-exports');
    minterSelect = document.querySelector<HTMLSelectElement>('#minter-select');
    messageInput = document.querySelector<HTMLInputElement>('#message-input');
    exportProgress = document.querySelector<HTMLDivElement>('#export-progress');
    progressLabel = document.querySelector<HTMLDivElement>('#progress-label');
    progressFill = document.querySelector<HTMLDivElement>('#progress-fill');
    progressPercent = document.querySelector<HTMLDivElement>('#progress-percent');
  }

  if (!cardContainer || !chartContainer) return;

  // Load export modules (only when enabled)
  if (exportEnabled) {
    await loadExportModules();
  }

  // Chart instance reference
  let chart: ReturnType<typeof createHistoricalChart> | null = null;

  // ============================================
  // Event Listeners
  // ============================================

  // Download buttons (only when export is enabled)
  if (exportEnabled) {
    downloadPng?.addEventListener('click', handleDownloadPng);
    downloadMp4?.addEventListener('click', handleDownloadMP4);
    saveExports?.addEventListener('click', handleSaveExports);
  }

  // Quick nav buttons
  btnPrev?.addEventListener('click', () => navigateDay(-1));
  btnNext?.addEventListener('click', () => navigateDay(1));

  // Prediction modal
  const predictBtn = document.getElementById('predict-trigger');
  const modalContainer = document.getElementById('prediction-modal-container');
  
  function renderAndBind(): void {
    if (!modalContainer) return;
    modalContainer.innerHTML = renderModal();
    bindModalEvents({
      onClose: closePredictionModal,
      onStateChange: renderAndBind,
    });
  }

  async function openPredictionModal(): Promise<void> {
    if (!modalContainer) return;
    
    // Get current ETH price from latest candle
    const latestCandle = state.historicalData[state.historicalData.length - 1];
    const currentPrice = latestCandle?.ohlc?.close || 3000; // fallback
    setCurrentPrice(currentPrice);
    
    // Show loading, then init
    modalContainer.innerHTML = renderModal();
    await initModal();
    renderAndBind();
  }

  function closePredictionModal(): void {
    if (modalContainer) modalContainer.innerHTML = '';
    // Update button text in case user just submitted
    updatePredictButtonLabel();
  }

  predictBtn?.addEventListener('click', openPredictionModal);

  // Update button text based on user's prediction state
  async function updatePredictButtonLabel(): Promise<void> {
    if (!predictBtn) return;
    const label = await getPredictButtonLabel();
    predictBtn.textContent = label;
  }
  
  // Check on page load (non-blocking)
  updatePredictButtonLabel();

  // ============================================
  // Data Loading
  // ============================================

  // Check if export server is available (required for PNG/MP4 export)
  if (exportEnabled && checkExportServer) {
    checkExportServer().then(available => {
      state.exportServerAvailable = available;
      if (available) {
        console.log('[Export] Server available');
      } else {
        console.log('[Export] Server not running. Start with: cd server && node index.js');
      }
    });
  }

  loadHistoricalData();

  async function loadHistoricalData(): Promise<void> {
    try {
      state.isLoading = true;
      // Load full ETH history (static + live data for today)
      const data = await fetchHistoricalData();
      state.historicalData = data;
      state.isLoading = false;

      // Create chart - width will be calculated based on data length
      const chartHeight = window.innerWidth < 640 ? 180 : 240;

      chart = createHistoricalChart(chartContainer!, data, {
        onCandleClick: handleCandleClick,
        onCandleHover: handleCandleHover,
      }, {
        width: 800, // Minimum width, actual width calculated from data
        height: chartHeight,
        paddingLeft: 50,
        paddingRight: 15,
      });

      // Initialize notes (behind ?notes flag)
      if (showNotes) initNotes();

      // Check URL for date parameter, otherwise select most recent day
      if (data.length > 0) {
        const urlParams = new URLSearchParams(window.location.search);
        const dateParam = urlParams.get('date');

        let selectedIdx = data.length - 1; // Default to most recent

        if (dateParam) {
          // Find index by date
          const foundIdx = data.findIndex(d => d.date === dateParam);
          if (foundIdx >= 0) {
            selectedIdx = foundIdx;
          }
        }

        handleCandleClick(data[selectedIdx], selectedIdx);
        chart.setSelected(selectedIdx);
      }

      // Show hint now that chart is loaded
      showChartHint();

      // Hide hint on any chart interaction (scroll, wheel, touch)
      const chartWrapper = document.querySelector('.chart-wrapper');
      if (chartWrapper) {
        const hideOnInteraction = () => hideChartHint();
        chartWrapper.addEventListener('wheel', hideOnInteraction, { once: true });
        chartWrapper.addEventListener('touchstart', hideOnInteraction, { once: true });
        chartWrapper.addEventListener('mousedown', hideOnInteraction, { once: true });
      }

      // Check for winner banner (async, non-blocking)
      checkWinnerBanner();
    } catch {
      chartContainer!.innerHTML = `
        <div class="chart-error">FAILED TO LOAD DATA</div>
      `;
    }
  }

  // ============================================
  // Winner Banner
  // ============================================

  async function checkWinnerBanner(): Promise<void> {
    try {
      // Check if user is logged in
      const user = await getUser();
      if (!user) return;

      // Get auth token and fetch profile
      const token = await getAuthToken();
      if (!token) return;

      const res = await fetch('/api/predictions/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) return;

      const data = await res.json();
      
      // Show winner banner if user has a recent unclaimed win
      if (data.recentWin && !data.recentWin.claimed) {
        showWinnerBanner(data.recentWin as WinnerBannerData);
      }
    } catch (err) {
      // Silent fail - banner is not critical
      console.debug('[WinnerBanner] Check failed:', err);
    }
  }

  // ============================================
  // Event Handlers
  // ============================================

  async function handleCandleClick(data: DayData, index: number): Promise<void> {
    // Hide hint on user interaction (not on initial auto-selection)
    if (state.hasUserInteracted) {
      hideChartHint();
    }
    state.hasUserInteracted = true;

    state.date = data.date;
    state.selectedIndex = index;

    // Update URL with date param (without page reload)
    const url = new URL(window.location.href);
    url.searchParams.set('date', data.date);
    window.history.replaceState({}, '', url.toString());

    // Auto-select theme based on rarity (using percentage-based classification)
    const rarityConfig = getRarityConfig(data.change.percent, data.dayId, data.date);
    state.theme = rarityConfig.theme;

    // Set data immediately for quick render, then fetch intraday
    state.data = { ...data, intraday: [], intradayVolumes: [] };
    renderCard();
    updateStatsBar();
    updateRarityLabel();

    // Refresh notes for the selected date (non-blocking)
    if (showNotes) refreshNotes(data.date);

    // Fetch real hourly data for intraday chart (prices + volumes)
    const intradayData = await fetchIntradayData(data.date);
    if (state.date === data.date) {
      // Only update if still viewing the same day
      state.data = {
        ...data,
        intraday: intradayData.prices,
        intradayVolumes: intradayData.volumes,
      };
      renderCard();
    }
  }

  function hideChartHint(): void {
    const hint = document.querySelector('#chart-hint');
    if (hint) {
      hint.classList.add('hidden');
    }
  }

  function showChartHint(): void {
    const hint = document.querySelector('#chart-hint');
    if (hint) {
      hint.classList.remove('hidden');
    }
  }

  function handleCandleHover(_data: DayData | null, _index: number): void {
    // Hover info handled in chart component
  }

  async function handleDownloadPng(): Promise<void> {
    if (!exportArea || !state.data) return;

    if (!state.exportServerAvailable || !downloadCardAsPngServer) {
      alert('Export server not running. Start it with: cd server && node index.js');
      return;
    }

    setExportButtonsDisabled(true);
    try {
      const baseUrl = window.location.origin;
      const exportUrl = `${baseUrl}/export?date=${state.date}&theme=${state.theme}`;
      await downloadCardAsPngServer(exportUrl, state.date, state.theme, { scale: 2 });
    } catch (error) {
      console.error('PNG export failed:', error);
      alert('PNG export failed. Check server console for details.');
    } finally {
      setExportButtonsDisabled(false);
    }
  }

  async function handleDownloadMP4(): Promise<void> {
    if (!exportArea || !state.data) return;

    if (!state.exportServerAvailable || !downloadCardAsMP4Server) {
      alert('Export server not running. Start it with: cd server && node index.js');
      return;
    }

    setExportButtonsDisabled(true);
    showProgress(true);

    try {
      updateProgress('Connecting to export server...', 0);

      const baseUrl = window.location.origin;
      const exportUrl = `${baseUrl}/export?date=${state.date}&theme=${state.theme}`;

      await downloadCardAsMP4Server(exportUrl, state.date, state.theme, {
        fps: 30,
        duration: 8000,
        scale: 2,
        onProgress: (status) => {
          updateProgress(status, 50);
        },
      });
    } catch (error) {
      console.error('MP4 export failed:', error);
      alert('MP4 export failed. Check server console for details.');
    } finally {
      showProgress(false);
      setExportButtonsDisabled(false);
    }
  }

  async function handleSaveExports(): Promise<void> {
    if (!exportArea || !state.data) return;

    if (!state.exportServerAvailable || !saveExportsForMint) {
      alert('Export server not running. Start it with: npm run dev');
      return;
    }

    // Get today's date (UTC)
    const todayStr = new Date().toISOString().split('T')[0];

    // If today is selected, switch to yesterday (last completed candle)
    let saveDate = state.date;
    let saveTheme = state.theme;
    let saveData = state.data;
    let saveIndex = state.selectedIndex;

    if (state.date === todayStr) {
      // Find yesterday's candle
      const yesterdayIndex = state.historicalData.length - 2;
      if (yesterdayIndex >= 0) {
        const yesterdayData = state.historicalData[yesterdayIndex];
        saveDate = yesterdayData.date;
        saveData = yesterdayData;
        saveIndex = yesterdayIndex;
        // Recalculate theme for yesterday
        const rarityConfig = getRarityConfig(yesterdayData.change.percent, yesterdayData.dayId, yesterdayData.date);
        saveTheme = rarityConfig.theme;

        // Update UI to show yesterday
        handleCandleClick(yesterdayData, yesterdayIndex);
        chart?.setSelected(yesterdayIndex);
      }
    }

    // Build day data for metadata
    const rarityConfig = getRarityConfig(saveData.change.percent, saveData.dayId, saveData.date);

    // Calculate average volume for traits
    let avgVolume: number | undefined;
    if (saveIndex >= 0 && state.historicalData.length > 0) {
      const startIdx = Math.max(0, saveIndex - 30);
      const endIdx = saveIndex;
      if (endIdx > startIdx) {
        const volumeSlice = state.historicalData.slice(startIdx, endIdx);
        avgVolume = volumeSlice.reduce((sum, d) => sum + d.market.volume, 0) / volumeSlice.length;
      }
    }

    const traits = calculateTraits(saveData, avgVolume);

    const dayData: import('./export/server-export').SaveDayData = {
      dayId: saveData.dayId,
      ohlc: saveData.ohlc,
      change: saveData.change,
      direction: rarityConfig.direction,
      tier: rarityConfig.tier.charAt(0).toUpperCase() + rarityConfig.tier.slice(1),
      event: rarityConfig.mythicEvent === 'black_swan' ? 'Black Swan' :
             rarityConfig.mythicEvent === 'supernova' ? 'Supernova' :
             rarityConfig.mythicEvent === 'special' ? 'Special Event' : undefined,
      traits: {
        momentum: traits.momentum,
        volatility: traits.volatility,
        volume: traits.volume,
      },
    };

    // Attach winning note to export metadata
    try {
      const notesData = await fetchNotes(saveDate, { limit: 50, offset: 0 });
      if (notesData.winnerId) {
        const winner = notesData.notes.find(n => n.id === notesData.winnerId);
        if (winner) {
          dayData.note = {
            text: winner.text,
            wallet: winner.wallet,
            twitter: winner.twitter,
            voteCount: winner.voteCount,
            type: winner.type,
            agentName: winner.agentName,
          };
        }
      }
    } catch {
      // Non-fatal: export proceeds without note
    }

    setExportButtonsDisabled(true);
    showProgress(true);

    try {
      updateProgress(`Saving ${saveDate} to exports/...`, 10);

      const baseUrl = window.location.origin;
      const exportUrl = `${baseUrl}/export?date=${saveDate}&theme=${saveTheme}`;

      const minterValue = minterSelect?.value as 'Human' | 'Agent' | undefined;
      const messageValue = messageInput?.value?.trim() || undefined;
      const result = await saveExportsForMint(exportUrl, saveDate, saveTheme, {
        fps: 30,
        duration: 8000,
        scale: 2,
        minter: minterValue || undefined,
        message: messageValue,
        onProgress: (status) => {
          updateProgress(status, 50);
        },
      }, dayData);

      updateProgress('Done!', 100);
      const files = [result.files.png, result.files.mp4, result.files.metadata].filter(Boolean);
      alert(`Saved to exports/:\n${files.map(f => `• ${f}`).join('\n')}\n\nRun: npm run mint`);
    } catch (error) {
      console.error('Save failed:', error);
      alert('Save failed. Check server console for details.');
    } finally {
      showProgress(false);
      setExportButtonsDisabled(false);
    }
  }

  function setExportButtonsDisabled(disabled: boolean): void {
    downloadPng?.setAttribute('disabled', disabled ? 'true' : '');
    downloadMp4?.setAttribute('disabled', disabled ? 'true' : '');
    saveExports?.setAttribute('disabled', disabled ? 'true' : '');
    if (!disabled) {
      downloadPng?.removeAttribute('disabled');
      downloadMp4?.removeAttribute('disabled');
      saveExports?.removeAttribute('disabled');
    }
  }

  function showProgress(show: boolean): void {
    if (exportProgress) {
      exportProgress.style.display = show ? 'flex' : 'none';
    }
  }

  function updateProgress(label: string, percent: number): void {
    if (progressLabel) progressLabel.textContent = label;
    if (progressFill) progressFill.style.width = `${percent}%`;
    if (progressPercent) progressPercent.textContent = `${Math.round(percent)}%`;
  }

  function navigateDay(direction: number): void {
    if (state.historicalData.length === 0) return;

    let newIndex = state.selectedIndex + direction;
    if (newIndex < 0) newIndex = 0;
    if (newIndex >= state.historicalData.length) newIndex = state.historicalData.length - 1;

    if (newIndex !== state.selectedIndex) {
      const data = state.historicalData[newIndex];
      handleCandleClick(data, newIndex);
      chart?.setSelected(newIndex);
    }
  }


  // ============================================
  // Today's Card Status (Live)
  // ============================================

  // Countdown timer is now in ui/layout.ts (shared across all pages)

  // ============================================
  // UI Updates
  // ============================================

  function renderCard(): void {
    if (!cardContainer || !state.data) return;

    const config = buildConfig(state.data);

    // Calculate 30-day average volume for volume trait
    let avgVolume: number | undefined;
    if (state.selectedIndex >= 0 && state.historicalData.length > 0) {
      const startIdx = Math.max(0, state.selectedIndex - 30);
      const endIdx = state.selectedIndex;
      if (endIdx > startIdx) {
        const volumeSlice = state.historicalData.slice(startIdx, endIdx);
        avgVolume = volumeSlice.reduce((sum, d) => sum + d.market.volume, 0) / volumeSlice.length;
      }
    }

    // Calculate hidden traits
    const traits = calculateTraits(state.data, avgVolume);

    renderCardTo(cardContainer, state.data, config, traits);
  }

  function updateStatsBar(): void {
    if (!state.data) return;

    const statDate = document.querySelector('#stat-date');
    const statDayId = document.querySelector('#stat-dayid');
    const statClose = document.querySelector('#stat-close');
    const statChange = document.querySelector('#stat-change');

    if (statDate) statDate.textContent = formatDisplayDate(state.data.date);
    if (statDayId) statDayId.textContent = formatDayId(state.data.dayId);
    if (statClose) statClose.textContent = `$${formatPrice(state.data.ohlc.close)}`;
    if (statChange) {
      statChange.textContent = formatPercentChange(state.data.change.percent);
      statChange.className = `stat-value ${state.data.change.percent >= 0 ? 'positive' : 'negative'}`;
    }
  }

  function updateRarityLabel(): void {
    const label = document.querySelector('#card-rarity') as HTMLElement;
    if (!label || !state.data) return;

    // Use percentage-based classification
    const rarity = getDisplayRarity(
      state.data.change.percent,
      state.data.dayId
    );
    label.textContent = rarity.display;
    label.style.color = rarity.color;
    label.style.textShadow = `0 0 8px ${rarity.color}`;
  }

}

// ============================================
// Helpers
// ============================================

function buildConfig(data: DayData): CardConfig {
  // Use percentage-based rarity config
  const rarityConfig = getRarityConfig(data.change.percent, data.dayId, data.date);

  // Legendary tier gets molten-glow trait automatically
  const legendaryTraits =
    rarityConfig.tier === 'legendary' && !rarityConfig.mythicEvent
      ? ['molten-glow']
      : undefined;

  return {
    theme: rarityConfig.theme,
    tier: rarityConfig.tier,
    direction: rarityConfig.direction,
    mythicEvent: rarityConfig.mythicEvent,
    traits: legendaryTraits,
  };
}

// ============================================
// Start Application
// ============================================

init();
